import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Calendar, Quote, ExternalLink } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimations';

// Animated Counter Hook - counts from 0 to target with acceleration
const useCountUp = (target, duration = 2000) => {
  // Starts at the target so the pre-rendered HTML and the first client render match;
  // it resets to 0 on the client until the counter scrolls into view.
  const [count, setCount] = useState(target);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !hasAnimated) {
            setCount(0);
          } else if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            const startTime = Date.now();
            const animate = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              // Ease-out cubic for acceleration effect (fast at start, slows at end)
              const easeOut = 1 - Math.pow(1 - progress, 3);
              const currentCount = Math.floor(easeOut * target);
              
              setCount(currentCount);
              
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                setCount(target);
              }
            };
            
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  return { count, elementRef, isComplete: count >= target };
};

// MyBuilder badge component
const MyBuilderBadge = () => (
  <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: '#f0f7f0',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#2e7d32'
  }}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#2e7d32"/>
    </svg>
    Verified on MyBuilder
  </div>
);

// Google badge component
const GoogleBadge = () => (
  <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: '#f8f9fa',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#5f6368'
  }}>
    <svg viewBox="0 0 24 24" width="14" height="14">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
    Verified on Google
  </div>
);

export const Testimonials = () => {
  useScrollAnimations();
  
  // Animated counter for happy customers
  const { count: customerCount, elementRef: customerCountRef, isComplete } = useCountUp(100, 2500);
  
  const testimonials = [
    // Google Reviews
    {
      name: 'Sarah M.',
      location: 'Romford',
      date: 'Feb 2026',
      rating: 5,
      service: 'Front door lock replacement',
      review: 'Absolutely fantastic service from Joe. He arrived promptly, diagnosed the issue with my front door lock quickly, and had it replaced within the hour. Very professional and reasonably priced. Highly recommend Royal Security!',
      source: 'google'
    },
    {
      name: 'David T.',
      location: 'Hornchurch',
      date: 'Feb 2026',
      rating: 5,
      service: 'Window mechanism repair',
      review: 'Called Royal Security about a stuck window that wouldn\'t close properly. Joe came out the same day and fixed it on the spot. Great communication throughout and fair pricing. Will definitely use again.',
      source: 'google'
    },
    {
      name: 'James P.',
      location: 'Upminster',
      date: 'Jan 2026',
      rating: 5,
      service: 'Multipoint lock repair',
      review: 'Our front door lock had been playing up for weeks. Joe diagnosed the problem immediately and repaired the multipoint lock mechanism. Door now works perfectly. Professional, knowledgeable and great value.',
      source: 'google'
    },
    {
      name: 'Michelle R.',
      location: 'Dagenham',
      date: 'Jan 2026',
      rating: 5,
      service: 'Double glazing repair',
      review: 'Had misted up double glazing that needed replacing. Joe gave an honest quote, no pressure sales, and did a brilliant job. The windows look brand new. Recommended to all my neighbours!',
      source: 'google'
    },
    {
      name: 'Robert K.',
      location: 'Ilford',
      date: 'Jan 2026',
      rating: 5,
      service: 'Patio door adjustment',
      review: 'Patio door was dragging badly and wouldn\'t lock properly. Joe adjusted the whole mechanism and now it slides and locks perfectly. Quick, efficient and very reasonable. Top service!',
      source: 'google'
    },
    // MyBuilder Reviews - Newest first
    {
      name: 'Alvin',
      location: 'East Ham, London',
      date: '13 Feb 2026',
      rating: 5,
      service: 'Tight front door',
      review: 'Turned up very quickly after accepting the job for fixing a sticking front door. Was very professional and did great work.',
      source: 'mybuilder'
    },
    {
      name: 'MyBuilder User',
      location: 'Romford',
      date: '8 Feb 2026',
      rating: 5,
      service: 'Fix stiff lock on front door',
      review: 'Quick response and professional service. Lock now works perfectly.',
      source: 'mybuilder'
    },
    {
      name: 'Zakir',
      location: 'Ilford',
      date: '7 Feb 2026',
      rating: 5,
      service: 'Double glazed sash style window',
      review: 'Thank you for fixing our broken window! Great job!',
      source: 'mybuilder'
    },
    {
      name: 'Penny',
      location: 'Basildon',
      date: '6 Feb 2026',
      rating: 5,
      service: 'Pocket door repair',
      review: 'Very impressed with this company. The job was located in a tenanted property and they completed the job very quickly. The pocket door repair looks very professional and they have created an access panel in case of future problems. The finished job looks excellent. They provided photos of the finished job and I will certainly use this company again.',
      source: 'mybuilder'
    },
    {
      name: 'PULKIT',
      location: 'Hornchurch',
      date: '2 Feb 2026',
      rating: 5,
      service: 'Locks jamming',
      review: 'Professional workmanship and excellent communication. Will highly recommend.',
      source: 'mybuilder'
    },
    {
      name: 'Beatrice Charalambous',
      location: 'Enfield',
      date: '2 Feb 2026',
      rating: 5,
      service: 'Doors shaved and placed back after carpet fitted',
      review: 'Done the job very well',
      source: 'mybuilder'
    },
    {
      name: 'Eoin',
      location: 'Bethnal Green, London',
      date: '31 Jan 2026',
      rating: 5,
      service: 'Repair / replace lock / handle on upvc window',
      review: 'Joe was excellent. He turned up on time & did the job very quickly. Clear and prompt communication throughout. He even dropped a spare key over to me on the other side of London a week afterwards when I asked. I would hire him again and I would recommend him.',
      source: 'mybuilder'
    },
    {
      name: 'Emma Harris',
      location: 'Chelmsford',
      date: '31 Jan 2026',
      rating: 5,
      service: 'Buy and replace uPVC window hinges',
      review: 'Joe was clear and prompt with communication. He did the job well (changed some window hinges) and I was pleased with him and his work. I would hire him again and I would recommend him and his company.',
      source: 'mybuilder'
    },
    {
      name: 'Salman',
      location: 'Romford',
      date: '31 Jan 2026',
      rating: 5,
      service: 'Wooden window repair – hinges & alignment',
      review: 'Joe did a great job fixing our wooden windows — they now close properly and the draught has gone. He was friendly, professional, and clearly very knowledgeable. Turned up on time, explained what he was doing, and left everything tidy. Would definitely recommend and would happily use him again.',
      source: 'mybuilder'
    },
    {
      name: 'Kerry',
      location: 'Colchester',
      date: '31 Jan 2026',
      rating: 5,
      service: 'Repair or Replace 1x Wooden Back Door',
      review: 'Joe was brilliant. Knowledgeable from the off, supplied and fitted a lovely new door for us and just cracked on. He even took a look at our front doors to give some free advice, which is why we will be using him again when we look to get them sorted. Would definitely recommend!',
      source: 'mybuilder'
    },
    {
      name: 'Mo',
      location: 'Hornchurch',
      date: '27 Jan 2026',
      rating: 5,
      service: 'Need to replace existing lock with a new one',
      review: 'Joe from Royal Security was just excellent, professional, caring, very friendly & polite has completed what I asked him to do very smoothly and efficiently, definitely highly recommend Royal Security and for sure will use him again.',
      source: 'mybuilder'
    },
    {
      name: 'Helen',
      location: 'Wickford',
      date: '27 Jan 2026',
      rating: 5,
      service: 'Replace cracked window in flat',
      review: 'Joe was super professional and helpful! Replaced my window without any issue at all. Absolutely no complaints, would hire him again =)',
      source: 'mybuilder'
    },
    {
      name: 'Paul',
      location: 'Colchester',
      date: '27 Jan 2026',
      rating: 5,
      service: 'PVC window that will not stay closed or lock - Repair or Replace',
      review: 'Excellent service turned up when agreed to had parts on van for a first time fix i will be using Joe in future for all works to window repairs',
      source: 'mybuilder'
    },
    {
      name: 'Mohammed',
      location: 'Canary Wharf, London',
      date: '24 Jan 2026',
      rating: 5,
      service: 'Replace assa thumbturn on front door',
      review: 'Fast, professional service and great communication from start to finish. Friendly guy and very accommodating when I asked for an extra change. Would happily use again.',
      source: 'mybuilder'
    },
    {
      name: 'MyBuilder User',
      location: 'Stratford, London',
      date: '23 Jan 2026',
      rating: 5,
      service: 'Open and repair pvc door',
      review: 'Excellent service from Royal Security. Professional and efficient work.',
      source: 'mybuilder'
    },
    {
      name: 'Tinotenda',
      location: 'Romford',
      date: '21 Jan 2026',
      rating: 5,
      service: 'Repair window hinges (not closing properly) and fix window handle',
      review: 'Excellent service. Very friendly and got the job well done. Will definitely be using again in the future!',
      source: 'mybuilder'
    },
    {
      name: 'Tim',
      location: 'Bexleyheath',
      date: '21 Jan 2026',
      rating: 5,
      service: 'Front door allowing draught',
      review: 'Fantastic job, sorted out issue with Aluminium door alignment and also serviced door. Highly recommended, arrived on time and most importantly the price quoted was fair.',
      source: 'mybuilder'
    },
    {
      name: 'Tommy',
      location: 'Brentwood',
      date: '16 Jan 2026',
      rating: 5,
      service: 'Key won\'t turn to lock door',
      review: 'Joe identified and explained the issue before fixing it quickly at a good price, would definitely recommend.',
      source: 'mybuilder'
    },
    {
      name: 'Ruben',
      location: 'Canary Wharf, London',
      date: '16 Jan 2026',
      rating: 5,
      service: 'Balcony door does not sit flush',
      review: 'Excellent!!!!, good speedy response',
      source: 'mybuilder'
    },
    {
      name: 'Filippo',
      location: 'Clapham Junction, London',
      date: '14 Jan 2026',
      rating: 5,
      service: 'Replace window handler',
      review: 'Very efficient and cost effective',
      source: 'mybuilder'
    },
    {
      name: 'Abu',
      location: 'Upper Edmonton, London',
      date: '13 Jan 2026',
      rating: 5,
      service: 'Repair window lock',
      review: 'Had a very good experience with Royal security. Professional, transparent and quick. Happy with the outcome and would use their services again.',
      source: 'mybuilder'
    },
    {
      name: 'MyBuilder User',
      location: 'Stoke Newington, London',
      date: '13 Jan 2026',
      rating: 5,
      service: 'Fix Draughty Sash Window',
      review: 'Quick, clear, honest, and even knocked some money off when the job turned out to be simpler than expected. Strong recommend!',
      source: 'mybuilder'
    },
    {
      name: 'MyBuilder User',
      location: 'Islington, London',
      date: '10 Jan 2026',
      rating: 5,
      service: 'Replace one pane in large sash',
      review: 'Our experience with Royal security was excellent. The price was very reasonable and the job was carried out with care and attention. I highly recommended them. We will be using them again.',
      source: 'mybuilder'
    },
    {
      name: 'Luke Edwards',
      location: 'Wanstead, London',
      date: '9 Jan 2026',
      rating: 5,
      service: 'Repair and maintenance to 3 sets of windows PVC Leyton',
      review: 'Polite. Quick. Approachable',
      source: 'mybuilder'
    },
    {
      name: 'Barun Shaw',
      location: 'Canning Town, London',
      date: '9 Jan 2026',
      rating: 5,
      service: 'Repair handle/ lock of balcony French door',
      review: 'Job well done and very professional. Happy with the service provided.',
      source: 'mybuilder'
    },
    {
      name: 'Edward D',
      location: 'Hackney Wick, London',
      date: '8 Jan 2026',
      rating: 5,
      service: 'Repair and refix a bedroom door',
      review: 'Was responsive with messages, quick with the work upon arrival and delivered a great final result for a very reasonable price. I was very satisfied with the job and would engage again for future work.',
      source: 'mybuilder'
    },
    {
      name: 'Chike Okonji',
      location: 'Barking',
      date: '8 Jan 2026',
      rating: 5,
      service: 'Open bedroom door',
      review: 'Punctual and able to repair in a timely manner. Great value for money and will be using him if I required repairs in the future.',
      source: 'mybuilder'
    },
    {
      name: 'Ollie',
      location: 'London',
      date: '8 Jan 2026',
      rating: 5,
      service: 'Glazing',
      review: 'Quick, reliable and professional, I\'ve used Joe a few times when needed and he\'s been effortless to deal with, highly recommend!',
      source: 'mybuilder'
    },
    {
      name: 'Claire',
      location: 'London',
      date: '8 Jan 2026',
      rating: 5,
      service: 'Front Door',
      review: 'The lock on front door was broken my daughter was locked out the house, Joe was very professional and changed my lock and door cylinder and have had no issue since, highly recommend 👍',
      source: 'mybuilder'
    }
  ];

  // Shuffle after mount (not during render) so the pre-rendered order matches the first client render
  const [shuffledReviews, setShuffledReviews] = useState(testimonials);
  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') return;
    setShuffledReviews([...testimonials].sort(() => Math.random() - 0.5));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container">
          <div style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: 'rgba(200, 168, 96, 0.2)',
            borderRadius: '6px',
            color: 'var(--royal-gold)',
            fontSize: '0.9rem',
            fontWeight: '600',
            marginBottom: '20px',
            border: '1px solid rgba(200, 168, 96, 0.3)'
          }}>
            Verified 5-Star Reviews
          </div>
          <h1 className="royal-heading-xl" style={{ color: 'var(--royal-white)', marginBottom: '16px' }}>
            Customer Reviews
          </h1>
          <p style={{ fontSize: '1.25rem', maxWidth: '700px', color: 'rgba(255, 255, 255, 0.9)' }}>
            See what our customers say about Royal Security's professional service, quality workmanship, and reliability.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section style={{
        background: 'var(--royal-gold)',
        padding: '32px 0'
      }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '32px',
            textAlign: 'center'
          }}>
            <div ref={customerCountRef}>
              <div style={{ 
                fontSize: '3rem', 
                fontWeight: '700', 
                color: 'var(--royal-navy)', 
                marginBottom: '8px',
                fontVariantNumeric: 'tabular-nums'
              }}>
                {isComplete ? '100+' : customerCount}
              </div>
              <div style={{ fontSize: '1rem', color: 'var(--royal-navy)', fontWeight: '500' }}>
                Happy Customers
              </div>
            </div>
            <div>
              <div style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--royal-navy)', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                5.0
                <Star size={36} fill="var(--royal-navy)" />
              </div>
              <div style={{ fontSize: '1rem', color: 'var(--royal-navy)', fontWeight: '500' }}>
                Average Rating
              </div>
            </div>
            <div>
              <div style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--royal-navy)', marginBottom: '8px' }}>
                100%
              </div>
              <div style={{ fontSize: '1rem', color: 'var(--royal-navy)', fontWeight: '500' }}>
                Would Recommend
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '32px'
          }}>
            {shuffledReviews.map((testimonial, index) => (
              <div key={index} className="scroll-animate" style={{
                background: 'var(--royal-white)',
                padding: '32px',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                position: 'relative',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                e.currentTarget.style.borderColor = 'var(--royal-gold)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'var(--border-color)';
              }}
              >
                {/* Quote Icon */}
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  opacity: 0.1
                }}>
                  <Quote size={48} style={{ color: 'var(--royal-navy)' }} />
                </div>

                {/* Rating */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="var(--royal-gold)" color="var(--royal-gold)" />
                  ))}
                </div>

                {/* Service Type */}
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: 'var(--royal-navy)',
                  marginBottom: '16px',
                  lineHeight: '1.4'
                }}>
                  {testimonial.service}
                </h3>

                {/* Review Text */}
                <p style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.7',
                  color: 'var(--text-secondary)',
                  marginBottom: '16px',
                  fontStyle: 'italic'
                }}>
                  "{testimonial.review}"
                </p>

                {/* Verified Badge */}
                <div style={{ marginBottom: '16px' }}>
                  {testimonial.source === 'google' ? <GoogleBadge /> : <MyBuilderBadge />}
                </div>

                {/* Customer Info */}
                <div style={{
                  paddingTop: '16px',
                  borderTop: '1px solid var(--border-color)'
                }}>
                  <div style={{ fontWeight: '600', color: 'var(--royal-navy)', marginBottom: '8px' }}>
                    {testimonial.name}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MapPin size={14} />
                      {testimonial.location}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={14} />
                      {testimonial.date}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leave a Review CTA */}
      <section style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        padding: '60px 0',
        borderTop: '1px solid #e8eaed'
      }}>
        <div className="royal-container">
          <div className="scroll-animate" style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            {/* Google Logo */}
            <div style={{ marginBottom: '20px' }}>
              <svg viewBox="0 0 24 24" width="48" height="48" style={{ margin: '0 auto' }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#202124',
              marginBottom: '12px'
            }}>
              Had a Great Experience?
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#5f6368',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              We'd love to hear from you! Your review helps other customers find trusted door and window specialists.
            </p>

            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <a
                href="https://g.page/r/CadT7JoatDE9EBM/review"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: '#1a73e8',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(26, 115, 232, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1557b0';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#1a73e8';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Star size={20} />
                Leave Us a Google Review
              </a>
              
              <a
                href="https://www.google.co.uk/search?ibp=gwp;0,7&q=Royal+Security&ludocid=4409503536533754791&lsig=AB86z5UO-cgB8oM65xOR6-WXrpix#lkt=LocalPoiReviews"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: 'white',
                  color: '#1a73e8',
                  border: '2px solid #dadce0',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#1a73e8';
                  e.currentTarget.style.background = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#dadce0';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <ExternalLink size={20} />
                View All Google Reviews
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container" style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '700',
            marginBottom: '24px',
            color: 'var(--royal-white)'
          }}>
            Experience the Royal Security Difference
          </h2>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '600px',
            margin: '0 auto 32px'
          }}>
            Join our satisfied customers and experience professional, reliable service.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary" style={{ background: 'var(--royal-gold)', color: 'var(--royal-navy)' }}>
              Get a Quote
            </Link>
            <a href="tel:07710613783" className="royal-btn-light">
              Call 07710 613783
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
